import { motion } from "framer-motion";
import { Heart, Instagram, MessageCircle, Mail } from "lucide-react";
import { PRODUCT } from "./data";
import { LANDING } from "@/constants/testIds";

function FooterBrandMark() {
  return (
    <svg
      viewBox="0 0 40 40"
      className="w-10 h-10 text-violet-200"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path
        d="M28 5a15 15 0 1 0 7 27 12.5 12.5 0 0 1-7-27z"
        fill="currentColor"
        opacity="0.95"
      />
      <path
        d="M12 10 L13.1 13.5 L17 14.5 L13.1 15.5 L12 19 L10.9 15.5 L7 14.5 L10.9 13.5 Z"
        fill="currentColor"
      />
      <circle cx="18" cy="9" r="0.9" fill="currentColor" />
      <circle cx="8" cy="25" r="0.7" fill="currentColor" opacity="0.7" />
    </svg>
  );
}

function MoonStarMini() {
  return (
    <svg
      viewBox="0 0 24 24"
      className="w-6 h-6 text-[#6d28d9]"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path
        d="M9.5 2.518a9.5 9.5 0 1 0 11.982 11.982 8 8 0 0 1-11.982-11.982z"
        fill="currentColor"
      />
      <path
        d="M16.5 3 Q16.5 6.5 20 6.5 Q16.5 6.5 16.5 10 Q16.5 6.5 13 6.5 Q16.5 6.5 16.5 3 Z"
        fill="currentColor"
      />
    </svg>
  );
}

export default function Footer() {
  return (
    <footer className="relative bg-gradient-to-b from-[#0a0218] to-black border-t border-violet-500/15 pt-20 pb-10 overflow-hidden">
      <div className="absolute -top-32 left-1/2 -translate-x-1/2 w-[700px] h-[400px] rounded-full bg-violet-500/10 blur-[140px] pointer-events-none" />
      <div className="absolute top-0 left-1/2 -translate-x-1/2 flex items-center gap-4 text-[#6d28d9]/60 -translate-y-1/2">
        <span className="h-px w-24 bg-gradient-to-r from-transparent to-violet-400/40" />
        <MoonStarMini />
        <span className="h-px w-24 bg-gradient-to-l from-transparent to-violet-400/40" />
      </div>

      <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 mb-16">
          <div className="lg:col-span-5">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              data-testid={LANDING.footerLogo}
              className="flex items-center gap-4 mb-7"
            >
              <span className="relative flex items-center justify-center w-14 h-14 rounded-full border border-violet-300/30 bg-gradient-to-br from-violet-500/20 to-transparent">
                <FooterBrandMark />
                <span className="absolute inset-0 rounded-full bg-violet-400/15 blur-md -z-10" />
              </span>
              <div className="flex flex-col leading-tight">
                <span className="font-display italic text-2xl text-white">
                  {PRODUCT.name}
                </span>
                <span className="text-[10px] tracking-[0.22em] uppercase text-[#6d28d9]/75 mt-1">
                  {PRODUCT.subtitle}
                </span>
              </div>
            </motion.div>
            <p className="text-violet-200/70 text-[15px] leading-relaxed max-w-md font-display italic">
              Porque cada família é única.
              <span className="block text-slate-400/80 text-[13px] not-italic font-sans mt-2 leading-relaxed">
                Acompanhamento respeitoso, baseado em evidências, para você conduzir o sono do seu bebê com autonomia e leveza.
              </span>
            </p>
          </div>

          <div className="lg:col-span-7 grid sm:grid-cols-3 gap-10">
            <div>
              <h4 className="text-[#6d28d9] text-[11px] uppercase tracking-[0.22em] font-bold mb-5">Navegar</h4>
              <ul className="space-y-3 text-slate-300/80 text-[14px]">
                <li><a href="#top" className="hover:text-violet-200 transition">Início</a></li>
                <li><a href="#" className="hover:text-violet-200 transition">Metodologia</a></li>
                <li><a href="#" className="hover:text-violet-200 transition">Sobre a Mariana</a></li>
                <li><a href="#" className="hover:text-violet-200 transition">Perguntas frequentes</a></li>
              </ul>
            </div>

            <div>
              <h4 className="text-[#6d28d9] text-[11px] uppercase tracking-[0.22em] font-bold mb-5">Suporte</h4>
              <ul className="space-y-3 text-slate-300/80 text-[14px]">
                <li className="flex items-center gap-2">
                  <MessageCircle className="w-3.5 h-3.5 text-[#a78bfa]" />
                  <a href={PRODUCT.whatsappUrl} target="_blank" rel="noopener noreferrer" className="hover:text-violet-200 transition">WhatsApp</a>
                </li>
                <li className="flex items-center gap-2">
                  <Mail className="w-3.5 h-3.5 text-[#a78bfa]" />
                  <a href="mailto:atendimento@marimello.com.br" className="hover:text-violet-200 transition">atendimento@marimello.com.br</a>
                </li>
                <li><a href="#" className="hover:text-violet-200 transition">Política de reembolso</a></li>
              </ul>
            </div>

            <div>
              <h4 className="text-[#6d28d9] text-[11px] uppercase tracking-[0.22em] font-bold mb-5">Acompanhe</h4>
              <div className="flex items-center gap-3 mb-5">
                <a href={PRODUCT.instagramUrl} target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full border border-violet-400/30 flex items-center justify-center text-[#6d28d9] hover:bg-violet-500/15 hover:border-violet-300/70 transition" aria-label="Instagram">
                  <Instagram className="w-4 h-4" />
                </a>
                <a href={PRODUCT.whatsappUrl} target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full border border-violet-400/30 flex items-center justify-center text-[#6d28d9] hover:bg-violet-500/15 hover:border-violet-300/70 transition" aria-label="WhatsApp">
                  <MessageCircle className="w-4 h-4" />
                </a>
              </div>
              <p className="text-slate-400/70 text-[12px] leading-relaxed">
                Conteúdo, bastidores e novidades para mães que escolhem viver a maternidade com mais leveza.
              </p>
            </div>
          </div>
        </div>

        <div className="border-t border-violet-500/15 pt-8 flex flex-col md:flex-row items-center justify-between gap-5">
          <p className="text-violet-200/60 text-[12px] flex items-center gap-2 font-display italic">
            <Heart className="w-3.5 h-3.5 text-[#a78bfa]" fill="currentColor" />
            Feito com cuidado por {PRODUCT.author}
          </p>
          <p className="text-slate-400/50 text-[11px] text-center md:text-right">
            © {new Date().getFullYear()} {PRODUCT.author}. Todos os direitos reservados.
            <span className="block sm:inline mt-1 sm:mt-0 sm:ml-2">
              <a href="#" className="hover:text-[#6d28d9] transition">Termos de uso</a>{" "}·{" "}
              <a href="#" className="hover:text-[#6d28d9] transition">Política de privacidade</a>
            </span>
          </p>
        </div>
      </div>
    </footer>
  );
}
